/**
 * Browser-direct Sierra live-agent transport.
 *
 * Owns the cookie-authenticated calls to `/consumers-v3/utilities/chat`,
 * `/consumers-v3/utilities/poll`, and `/consumers-v3/utilities/upload`.
 * The widget supplies chat mechanics; this composable supplies brand/locale/release
 * and the generated utilities client.
 *
 * `messages[]` from chat is intentionally discarded — during escalation a human
 * owns the thread and poll delivers those replies as `role: "human_agent"`.
 */
export function useSierraChat() {
  const { utilities } = useApi()
  const { brand } = useRuntimeConfig().public
  const locale = useLocale()
  const { targetEnv } = useRuntimeConfig().public

  /** Non-prod → Staging; PROD → Production. Not a secret — public runtime config. */
  const release = targetEnv === 'PROD' ? 'Production' : 'Staging'

  /**
   * Send a shopper message during live-agent escalation.
   * `state` is required — omitting it makes Sierra mint a new conversation.
   */
  async function sendSierraMessage({ text, state }: { text: string, state: string }) {
    const res = await utilities.$chat({
      state,
      variables: {
        brand: String(brand),
        locale,
        supportContext: '',
      },
      clientEvent: {
        type: 'message',
        message: { content: text },
      },
      disableStreaming: true,
      release,
    })
    return { ok: true, state: res?.state ?? null }
  }

  /**
   * Long-poll / page Sierra live-agent events as NDJSON text.
   * Pass `responseType: 'text'` so ofetch does not JSON.parse multi-line NDJSON.
   */
  async function pollSierra(
    { state, cursor }: { state: string, cursor?: string | null },
    signal?: AbortSignal,
  ) {
    return utilities.$poll(
      {
        state,
        ...(cursor ? { livePollCursor: cursor } : {}),
      },
      { responseType: 'text', signal },
    )
  }

  /**
   * Upload warranty evidence images for the pre-escalation Sierra warranty flow.
   * One multipart POST with repeated `file` parts (Postman multi-file shape).
   * `state` comes from an earlier Agentforce SSE handoff (not live-agent poll).
   * Returns Sierra's file `key` for the host/widget to silent-message Agentforce.
   */
  async function uploadSierraFiles({
    state,
    files,
  }: {
    state: string
    files: Array<{ blob: Blob, name: string }>
  }): Promise<{ ok: boolean, key?: string, error?: string }> {
    if (!state) return { ok: false, error: 'Missing Sierra conversation state' }
    if (!files.length) return { ok: false, error: 'No files to upload' }

    try {
      const form = new FormData()
      form.append('state', state)
      for (const { blob, name } of files) {
        const file = blob instanceof File
          ? blob
          : new File([blob], name, { type: blob.type || 'image/jpeg' })
        form.append('file', file, name)
      }

      // FormData as body — ofetch sets multipart Content-Type with boundary.
      // Cast: generated UploadPayload is `{ state, file: File[] }`; FormData is the wire shape.
      const res = await utilities.$upload(form as any)
      if (!res?.key) return { ok: false, error: 'Upload response missing file key' }
      return { ok: true, key: res.key }
    }
    catch (err: any) {
      return {
        ok: false,
        error: err?.data?.errorDetails?.message || err?.message || 'Sierra file upload failed',
      }
    }
  }

  return { sendSierraMessage, pollSierra, uploadSierraFiles }
}
